/**
 * Who may open each route — the ONE map (Deep-test ADM-006).
 *
 * The sidebar used to carry its own `permission:` per item, and the routes
 * carried nothing, so a URL typed or bookmarked by someone the sidebar hid it
 * from opened the full page: an employee on /admin/users saw Invite User, and
 * after every request behind it was refused the screen said "No users found —
 * invite a new user". The server was right; the screen stated falsehoods about
 * the organisation's data. Only the super-admin pages guarded themselves.
 *
 * Now the routes and the sidebar both read THIS map:
 *   - App.tsx's SuspenseWrapper runs every route through <RouteGate>
 *     (components/Common/RouteGate.tsx), which shows "You don't have access to
 *     this page" before the page mounts — so no request is sent and no empty
 *     state can lie;
 *   - Sidebar.tsx offers an item only when canOpenRoute() says yes.
 * deeptestAdm006RoutesSayNoAccess.test.ts fails when a route in App.tsx is not
 * declared here, or when the sidebar grows a permission of its own again.
 *
 * WHICH PERMISSION. The one the page's own first read needs on the server —
 * so a user the gate lets in is one the page can actually load for. Where the
 * sidebar already gated a module, that is the same string it used. Detail
 * routes take their module's permission.
 *
 * The kinds:
 *   'public'     — no sign-in (login, public links, kiosks, portals with their
 *                  own session). The gate does not look at them.
 *   'signed-in'  — any signed-in user (dashboard, settings, profile).
 *   'super-admin'— the platform operator only (isPlatformOperator).
 *   { anyOf }    — any one of these permissions (hasPermFor, so the
 *                  maintenance:view / asset:view umbrellas accept either half).
 */
import { matchPath } from 'react-router-dom';
import { hasPermFor, isPlatformOperator, PERMISSIONS } from '@/utils/permissions';
import type { SiteScope, UserRole } from '@/types';
import { EMPLOYEE_NAV, EMPLOYEE_ONLY_NAV, isEmployeeExperience } from './employeeExperience';
import { NO_SITE_OPEN, TECH_ONLY_NAV, hasNoSite, isTechExperience } from './techExperience';

export type RouteAccess = 'public' | 'signed-in' | 'super-admin' | { anyOf: readonly string[]; allOf?: readonly string[] };

const p = (...anyOf: string[]): RouteAccess => ({ anyOf });
/** Every one of these permissions. */
const all = (...perms: string[]): RouteAccess => ({ anyOf: perms.slice(0, 1), allOf: perms });

const PM_ACCESS = p('tenant:edit', 'finance:approve_payment_runs', 'finance:approve_write_offs', 'finance:approve_deposit_refunds', 'finance:approve_credit_notes', 'finance:approve_cam_trueups');

export const ROUTE_ACCESS: Record<string, RouteAccess> = {
  // --- outside the app shell: no sign-in -----------------------------------
  '/login': 'public',
  '/register': 'public',
  '/forgot-password': 'public',
  '/set-password': 'public',
  '/reset-password': 'public',
  '/tenant-portal/*': 'public',
  '/contractor-portal/*': 'public',
  '/visitors/kiosk': 'public',
  '/visitor-pass/:token': 'public',
  '/contractor/register': 'public',
  '/contractor-register': 'public',
  '/rooms/:id/panel': 'public',
  '/signage': 'public',
  '/pricing': 'public',
  '/privacy/request': 'public',
  '/roadmap': 'public',
  '/public/link/:token': 'public',
  // Employee rebuild B28: the emailed data-export link; the token is the key.
  '/download/my-data/:token': 'public',
  '/visitor-checkin/:token': 'public',

  // --- every signed-in user ------------------------------------------------
  // No backend `dashboard:*` permission gates the dashboard.
  '/dashboard': 'signed-in',
  // Employee rebuild C1: the employee experience's home. Each card asks for
  // the permission its own read needs and is not drawn without it, so the
  // page opens for anyone signed in.
  '/my-day': 'signed-in',
  '/settings': 'signed-in',
  '/settings/siri-shortcuts': 'signed-in',
  '/profile': 'signed-in',
  // 🔴 NOT emergency:trigger, although the sidebar offers it only to those who
  // hold it. Every person an emergency alert reaches gets an in-app notice
  // linking HERE (services/emergency.py), and the page tells someone who
  // cannot raise alerts what to do instead. A "no access" page in the middle
  // of an evacuation would be the worst possible answer.
  '/safety': 'signed-in',
  // Employee rebuild C10: everyone may be in a building that is evacuated, and
  // GET /emergency/my-safety-info needs only a session.
  '/my-safety': 'signed-in',
  // The server decides (building's wardens or emergency:trigger) and 404s
  // anyone else; a warden holds no permission the gate could name.
  '/safety/roll-call/:alertId': 'signed-in',
  // GET /marketplace/categories and /listings need only a session.
  // FM Manager Q8: parking is not an FM surface; the page's reads are parking:view.
  '/marketplace/parking': p(PERMISSIONS.PARKING_VIEW),
  // Employee B3: every booking of mine in one list. Its reads are the
  // caller's own (/space/bookings/my, /space/room-bookings/mine,
  // /parking/my-bookings, /space/desks/recurring/my), which need a session and
  // nothing more than the booking permissions each section checks itself.
  // FM Manager Q8: the FM role holds no booking:* at all, so these two are
  // booking:view (held by every other role that books) rather than any session.
  '/my-bookings': p('booking:view'),
  '/book': p('booking:view'),
  // Employee rebuild C4/C5. Their first reads (GET /hybrid/compliance/me,
  // /hybrid/whos-in/people) need hybrid:view, held by every internal role.
  '/office-days': p(PERMISSIONS.HYBRID_VIEW),
  '/whos-in': p(PERMISSIONS.HYBRID_VIEW),
  // Employee rebuild C8: its read is GET /visitors/visits?mine=true, which
  // needs visitor:view; the Invite tab is offered only with visitor:create.
  '/my-visitors': p(PERMISSIONS.VISITORS_VIEW),
  // Employee rebuild C11: my own notifications; the list route needs a session only.
  '/notifications': 'signed-in',

  // --- modules: the sidebar's permission, detail routes alike -------------
  '/spaces': p(PERMISSIONS.SPACES_VIEW),
  // Employee rebuild A14: every optimiser read is space:edit OR
  // analytics:view on the server, so the page is too.
  '/space/optimiser': p('space:edit', PERMISSIONS.ANALYTICS_VIEW),
  '/helpdesk': p(PERMISSIONS.HELPDESK_VIEW),
  '/helpdesk/:id': p(PERMISSIONS.HELPDESK_VIEW),
  '/maintenance': p(PERMISSIONS.MAINTENANCE_VIEW),
  '/maintenance/work-orders/:id': p(PERMISSIONS.MAINTENANCE_VIEW),
  '/maintenance/checklists': p(PERMISSIONS.MAINTENANCE_VIEW),
  '/assets': p(PERMISSIONS.ASSETS_VIEW),
  '/assets/:id': p(PERMISSIONS.ASSETS_VIEW),
  // /scan is one page for every kind of sticker (Employee rebuild): a desk's QR
  // opens /scan?desk=… for anyone who books (B6); the attendant scans parking
  // passes with parking:edit (C1); a holder of helpdesk:create scans a sticker
  // to report a fault whether or not they may read the asset record (B18); the
  // asset and part look-ups stay asset:view.
  '/scan': p(PERMISSIONS.ASSETS_VIEW, 'booking:view', 'parking:edit', PERMISSIONS.HELPDESK_CREATE),
  // Employee rebuild C6 / C7. The page's first reads: GET /helpdesk/tickets
  // (helpdesk:view) and GET /maintenance/assets (asset:view_own|view_all).
  '/my-requests': p(PERMISSIONS.HELPDESK_VIEW),
  '/my-requests/:id': p(PERMISSIONS.HELPDESK_VIEW),
  '/my-equipment': p(PERMISSIONS.ASSETS_VIEW),
  '/visitors': p(PERMISSIONS.VISITORS_VIEW),
  '/visitors/:id': p(PERMISSIONS.VISITORS_VIEW),
  '/leases': p(PERMISSIONS.LEASES_VIEW),
  '/leases/:id': p(PERMISSIONS.LEASES_VIEW),
  '/contractors': p(PERMISSIONS.CONTRACTORS_VIEW),
  '/contractors/:id': p(PERMISSIONS.CONTRACTORS_VIEW),
  '/tenants': p(PERMISSIONS.TENANTS_VIEW),
  '/tenant-ops': p('tenant:edit', 'maintenance:assign', 'visitor:checkin', 'reception:desk'),
  '/parking': p(PERMISSIONS.PARKING_VIEW),
  '/wellbeing': p(PERMISSIONS.WELLBEING_VIEW),
  '/iot': p(PERMISSIONS.IOT_VIEW),
  '/iot/machines/:machineId': p(PERMISSIONS.IOT_VIEW),
  '/energy': p(PERMISSIONS.ENERGY_VIEW),
  '/compliance': p(PERMISSIONS.COMPLIANCE_VIEW),
  '/hybrid': p(PERMISSIONS.HYBRID_VIEW),
  '/analytics': p(PERMISSIONS.ANALYTICS_VIEW),
  // Its two reads, /dashboard/fm-alerts and /dashboard/executive, need
  // analytics:view.
  '/fm-mobile': p(PERMISSIONS.ANALYTICS_VIEW),
  '/marketplace/integrations': p(PERMISSIONS.ADMIN_VIEW),
  // FM Manager rebuild W1: the FM home (C1; GET /fm/home needs
  // helpdesk:view_all) and SLA & escalation settings (C28; GET /fm/settings
  // — editable with fm:contract_edit, read-only with maintenance:view_all).
  '/fm': p(PERMISSIONS.HELPDESK_VIEW_ALL),
  '/fm/settings': p(PERMISSIONS.FM_CONTRACT_EDIT, 'maintenance:view_all'),
  // W2: the FM tickets list and pop-up (C2–C7; GET /fm/tickets needs
  // helpdesk:view_all, as the home does).
  '/fm/tickets': p(PERMISSIONS.HELPDESK_VIEW_ALL),
  // W3: spot-checks (C8; GET /fm/spot-checks needs helpdesk:assign — an FM
  // Technician must not pass their own work, W3 decision 3).
  '/fm/spot-checks': p(PERMISSIONS.HELPDESK_ASSIGN),
  // W4: daily logbooks (C9; GET /fm/logbooks needs maintenance:work AND
  // helpdesk:edit — a contractor holds maintenance:work, not helpdesk:edit).
  '/fm/logbooks': all(PERMISSIONS.MAINTENANCE_WORK, PERMISSIONS.HELPDESK_EDIT),
  // W5: the PPM planner (C11; GET /fm/ppm needs maintenance:view_all) and
  // assets (C13; GET /fm/assets needs asset:view_all).
  '/fm/ppm': p('maintenance:view_all'),
  // FM Technician A7: also fm:view_costs on the server — the technician is
  // the one asset:view_all holder without it, and the page is the FM's.
  '/fm/assets': all('asset:view_all', PERMISSIONS.FM_VIEW_COSTS),
  // W6: permits and incidents (C14, C18; GET /fm/permits and /fm/incidents
  // need permit:view) and fire drills (C17; GET /fm/drills needs compliance:view).
  // FM Technician A7: the board and the list are the FM's pages — the server
  // asks for helpdesk:view_all on top (routers/fm_permits _BOARD). A
  // technician reads its own permits (/tech/permits/:id) and its own reports
  // (/tech/incident).
  '/fm/permits': all(PERMISSIONS.PERMIT_VIEW, PERMISSIONS.HELPDESK_VIEW_ALL),
  '/fm/incidents': all(PERMISSIONS.PERMIT_VIEW, PERMISSIONS.HELPDESK_VIEW_ALL),
  '/fm/drills': p(PERMISSIONS.COMPLIANCE_VIEW),
  // W7: vendors, AMC contracts and invoice check (C19, C21, C22; GET
  // /fm/vendors, /fm/amc and /fm/invoices need contractor:view).
  '/fm/vendors': p(PERMISSIONS.CONTRACTORS_VIEW),
  '/fm/amc': p(PERMISSIONS.CONTRACTORS_VIEW),
  '/fm/invoices': p(PERMISSIONS.CONTRACTORS_VIEW),
  // W9: the compliance calendar (C24; GET /fm/compliance needs compliance:view).
  '/fm/compliance': p(PERMISSIONS.COMPLIANCE_VIEW),
  // W10: Reports (C25) and the KPI board (C27) — GET /fm/reports and
  // /fm/kpis need fm:view_reports (owner decision F4: not reception); the
  // pack's own actions need fm:contract_edit, which the page reads from the
  // response.
  '/fm/reports': p(PERMISSIONS.FM_VIEW_REPORTS),
  '/fm/kpis': p(PERMISSIONS.FM_VIEW_REPORTS),
  // FM Technician C24 / C25 (R1): the designated-person register and the
  // technicians page — every read is fm:manage_technicians (routers/fm_people).
  '/fm/designated-persons': p(PERMISSIONS.FM_MANAGE_TECHNICIANS),
  '/fm/technicians': p(PERMISSIONS.FM_MANAGE_TECHNICIANS),
  // C26 (R2): proof needed to finish + failure codes (routers/fm_technicians_r2).
  '/fm/settings/proof': p(PERMISSIONS.FM_MANAGE_TECHNICIANS),
  // C27 (R3): the tools list and the locks view (GET /fm/tools, /fm/locks).
  '/fm/tools': p(PERMISSIONS.FM_MANAGE_TECHNICIANS),
  '/fm/locks': p(PERMISSIONS.FM_MANAGE_TECHNICIANS),

  // --- FM Technician (R1) ---------------------------------------------------
  // C2: the "No site yet" screen; anyone signed in may see it (the guard
  // sends a technician with no site here).
  '/tech/no-site': 'signed-in',
  // C14: reporting an incident is POST /fm/incidents (permit:view AND the
  // facilities team: helpdesk:work or helpdesk:edit); the site list is GET
  // /tech/checkin (maintenance:work).
  '/tech/incident': { anyOf: [PERMISSIONS.HELPDESK_WORK, PERMISSIONS.HELPDESK_EDIT],
                      allOf: [PERMISSIONS.PERMIT_VIEW, PERMISSIONS.MAINTENANCE_WORK] },
  // A permit I hold, asked for, or that covers my job — GET /fm/permits/{id}
  // is permit:view and 404s anyone else's. Opened from notifications and the
  // job page, not the menu.
  // R3 (B34): the page reads GET /tech/permits/{id} (maintenance:work AND
  // permit:view), and C11's list GET /tech/permits the same.
  '/tech/permits/:id': all(PERMISSIONS.PERMIT_VIEW, PERMISSIONS.MAINTENANCE_WORK),
  '/tech/permits': all(PERMISSIONS.PERMIT_VIEW, PERMISSIONS.MAINTENANCE_WORK),
  // R3 (B35, B36): C12 GET /tech/locks and C18 GET /tech/tools are maintenance:work.
  '/tech/locks': p(PERMISSIONS.MAINTENANCE_WORK),
  '/tech/tools': p(PERMISSIONS.MAINTENANCE_WORK),
  // R3 (B38): C13 emergency steps — GET/POST /tech/emergencies, PUT …/steps,
  // all maintenance:work.
  '/tech/emergency': p(PERMISSIONS.MAINTENANCE_WORK),

  // --- FM Technician (R2, slice S1) -----------------------------------------
  // C3 home: GET /tech/home. C5 the one job list: GET /tech/jobs. C6 the job:
  // GET /tech/jobs/{kind}/{id} — all maintenance:work (a ticket's job page
  // also needs helpdesk:work on the server; a technician holds both). Declaring
  // /tech makes it the technician's home (interpretation 87) and moves "Home"
  // and "My jobs" in the menu and the phone bar to these pages (83).
  '/tech': p(PERMISSIONS.MAINTENANCE_WORK),
  '/tech/jobs': p(PERMISSIONS.MAINTENANCE_WORK),
  '/tech/jobs/:kind/:id': p(PERMISSIONS.MAINTENANCE_WORK),
  // FM Technician R2, slice "Me" (B16, B17, B18, B24): every read and write on
  // these pages is maintenance:work (routers/tech_me.py) or the person's own
  // notifications / privacy routes, which every signed-in person may call.
  '/tech/alerts': p(PERMISSIONS.MAINTENANCE_WORK),
  '/tech/profile': p(PERMISSIONS.MAINTENANCE_WORK),
  '/tech/welcome': p(PERMISSIONS.MAINTENANCE_WORK),
  '/tech/sync': p(PERMISSIONS.MAINTENANCE_WORK),
  // --- FM Technician (R2, slice "Shift") ------------------------------------
  // C15 / C17: GET /tech/handover/draft, /tech/handovers, /tech/my-work and
  // /tech/history are maintenance:work.
  '/tech/handover': p(PERMISSIONS.MAINTENANCE_WORK),
  '/tech/my-work': p(PERMISSIONS.MAINTENANCE_WORK),
  // C16: GET /tech/lead/verify is maintenance:work AND fm:shift_lead (D6).
  '/tech/lead': all(PERMISSIONS.FM_SHIFT_LEAD, PERMISSIONS.MAINTENANCE_WORK),
  // FM Technician R2, slice S3 (B4, B10, B11): C7 scan + the asset page read
  // GET /tech/assets/by-code/{code}, /tech/assets/{id} and /history — all
  // maintenance:work. C8 Report fault is POST /tech/faults and its duplicate
  // check (maintenance:report_fault) beside /tech/home's site list
  // (maintenance:work).
  '/tech/scan': p(PERMISSIONS.MAINTENANCE_WORK),
  '/tech/assets/:id': p(PERMISSIONS.MAINTENANCE_WORK),
  '/tech/report-fault': all(PERMISSIONS.MAINTENANCE_WORK, PERMISSIONS.MAINTENANCE_REPORT_FAULT),
  // FM Technician R3, slice S8 (B30–B33, B42): C9 Log rounds and C10 a round
  // read GET /tech/rounds, /tech/rounds/{id} and /tech/meters — maintenance:work
  // AND a ticket permission (a bad reading files a ticket; routers/tech_rounds
  // uses the FM logbook page's own rule). Signing is offered only on
  // `can_sign` (fm:shift_lead), so no lead route is called without it.
  '/tech/rounds': { anyOf: [PERMISSIONS.HELPDESK_WORK, PERMISSIONS.HELPDESK_EDIT],
                    allOf: [PERMISSIONS.MAINTENANCE_WORK] },
  '/tech/rounds/:templateId': { anyOf: [PERMISSIONS.HELPDESK_WORK, PERMISSIONS.HELPDESK_EDIT],
                                allOf: [PERMISSIONS.MAINTENANCE_WORK] },
  // A site's safety officer can be anybody the FM appoints (an employee holds
  // no permit:view): GET /fm/incidents/{id} decides, and 404s everyone else.
  '/safety-incidents/:id': 'signed-in',

  // --- administration: what each page's list read needs on the server ------
  '/admin': p(PERMISSIONS.ADMIN_VIEW),
  '/admin/users': p(PERMISSIONS.ADMIN_MANAGE_USERS),          // GET /admin/users
  '/admin/roles': p(PERMISSIONS.ADMIN_MANAGE_ROLES),           // GET /admin/roles
  '/admin/workflows': p(PERMISSIONS.ADMIN_MANAGE_ORG),         // GET /admin/workflows
  '/admin/forms': p(PERMISSIONS.ADMIN_MANAGE_ORG),             // GET /admin/forms
  '/admin/notification-templates': p(PERMISSIONS.ADMIN_MANAGE_ORG), // GET /admin/notification-templates
  '/admin/email-sequences': p(PERMISSIONS.ADMIN_MANAGE_ORG),   // GET /admin/email-sequences
  // Reception R4: kiosk device codes and the desk's settings.
  // Property Manager rebuild: the landlord's screens. A property manager, an admin, or a holder of an approve right.
  '/pm': PM_ACCESS, '/pm/calendar': PM_ACCESS, '/pm/rent-roll': PM_ACCESS, '/pm/tenancies/new': PM_ACCESS, '/pm/tenancies/:id': PM_ACCESS, '/pm/tenancies/:id/edit': PM_ACCESS,
  '/pm/tenants': PM_ACCESS, '/pm/tenants/:id': PM_ACCESS, '/pm/billing': PM_ACCESS, '/pm/collections': PM_ACCESS, '/pm/deposits': PM_ACCESS, '/pm/approvals': PM_ACCESS, '/pm/profile': PM_ACCESS, '/pm/moves': PM_ACCESS, '/pm/cam': PM_ACCESS, '/pm/compliance': PM_ACCESS, '/pm/fm': PM_ACCESS, '/pm/owner-report': PM_ACCESS, '/pm/exports': PM_ACCESS,
  '/pm/no-building': 'signed-in',
  '/admin/reception': p('reception:settings'),
  '/reception': p('reception:desk'),
  '/reception/check-in': p('reception:desk'),
  '/reception/visitors': p('reception:desk'),
  '/reception/pre-register': p('reception:desk'),
  '/reception/approvals': p('reception:desk'),
  '/reception/watchlist': p('reception:desk'),
  '/reception/alerts': p('reception:desk'),
  '/reception/profile': p('reception:desk'),
  '/reception/registers': p('reception:desk'),
  '/reception/mailroom': p('reception:desk'),
  '/reception/gate-passes': p('reception:desk'),
  '/reception/contractor-workers': p('reception:desk'),
  '/reception/gate-passes/new': p('reception:desk'),
  '/reception/keys-cards': p('reception:desk'),
  '/reception/lost-found': p('reception:desk'),
  '/reception/blank-register': p('reception:desk'),
  // The named approver or the host: any signed-in person, the server checks the row.
  '/reception/gate-pass-decision/:id': 'signed-in',
  '/visitor-answer/:id': 'signed-in',
  '/reception/rooms': p('reception:desk'),
  '/reception/parking': p('reception:desk'),
  '/reception/emergency': p('reception:desk'),
  '/reception/roll-call': p('reception:desk'),
  '/reception/drills': p('reception:desk'),
  '/reception/daily-report': p('reception:desk'),
  '/reception/handover': p('reception:desk'),
  '/reception/visitor-log': p('visitor:export'),
  // Anyone may raise a private concern, and read who it goes to; the inbox's read
  // answers 404 for all but the Internal Committee.
  '/reception/concern': 'signed-in',
  '/reception/concerns': 'signed-in',
  '/visit-decision/:id': 'signed-in',
  // Reception R2: signed in with no building yet.
  '/reception/no-building': 'signed-in',
  '/admin/badge-designer': p(PERMISSIONS.ADMIN_MANAGE_ORG),    // GET /admin/visitor-types
  '/admin/reports': p(PERMISSIONS.ADMIN_VIEW),                 // GET /admin/reports
  '/admin/data-residency': p(PERMISSIONS.ADMIN_VIEW),          // GET /admin/data-residency
  // Reached from the Admin page, and gated as it is.
  '/admin/integrations': p(PERMISSIONS.ADMIN_VIEW),
  // The catering menu and the pantry's orders (Employee rebuild C14 / B11):
  // the menu writes and GET /space/catering/orders need space:edit.
  // The menu is space:edit; the orders tab alone is catering:order_status
  // (the front desk, Employee follow-up).
  '/admin/catering': p('space:edit', 'catering:order_status'),
  // Employee rebuild C14: GET /announcements is admin:manage_org OR
  // announcement:manage; the safety set-up reads with compliance:edit (or
  // emergency:trigger) and writes with compliance:edit.
  '/admin/announcements': p(PERMISSIONS.ADMIN_MANAGE_ORG, PERMISSIONS.ANNOUNCEMENT_MANAGE),
  '/admin/safety': p(PERMISSIONS.COMPLIANCE_EDIT),

  // --- the platform operator ----------------------------------------------
  '/super-admin/organisations': 'super-admin',
  '/super-admin/support': 'super-admin',
  '/super-admin/data-requests': 'super-admin',
};

type GateUser = { role: UserRole; permissions?: string[] | null; site_scope?: SiteScope | null } | null | undefined;

/** May this user open a route with this access? Deny by default. */
export function canOpen(user: GateUser, access: RouteAccess | undefined): boolean {
  if (access === undefined) return false;
  if (access === 'public') return true;
  if (!user) return false;
  if (access === 'signed-in') return true;
  if (access === 'super-admin') return isPlatformOperator(user);
  return access.anyOf.some((perm) => hasPermFor(user, perm))
    && (access.allOf ?? []).every((perm) => hasPermFor(user, perm));
}

/** May this user open the route declared at exactly `pattern` (a ROUTE_ACCESS key)? */
export function canOpenRoute(user: GateUser, pattern: string): boolean {
  // Reception rebuild R3: the desk has its own equipment and not the register.
  // Every read on /assets is asset:view_all, so opening it would only 403.
  if (pattern === '/assets' && hasPermFor(user, 'reception:desk') && !hasPermFor(user, 'asset:view_all')) return false;
  // Property Manager P3: the FM home and approvals are refused to this role on the server.
  if (pattern === '/fm' && user?.role === 'property_manager') return false;
  return canOpen(user, ROUTE_ACCESS[pattern]);
}

/**
 * Routes anyone signed in may OPEN, but the sidebar OFFERS only to some.
 *
 * /safety is deliberate: it is open to everyone because the
 * emergency notice links there, but as a nav item it is the place to RAISE an
 * alert, and offering that to every employee would be offering a control they
 * do not hold. The sidebar keeps exactly the audience it had.
 */
export const NAV_OFFER: Record<string, RouteAccess> = {
  '/safety': p(PERMISSIONS.EMERGENCY_TRIGGER),
  // Bug_121: My bookings is offered to whoever can book (booking:create). The
  // page opens on booking:view, so the executive, who reads bookings and
  // makes none, can still open it but is not offered an empty list.
  '/my-bookings': p('booking:create'),
};

/** Should the sidebar offer this route to this user? Opening it must be allowed too.
 *
 *  Employee B3: somebody with the employee experience (config/employeeExperience)
 *  is offered their short list only, and the employee-only items are offered
 *  to nobody else — so no other role's menu changes. */
export function canOfferInNav(user: GateUser, pattern: string): boolean {
  if (!canOpenRoute(user, pattern)) return false;
  // C2: with no site yet, the menus offer only what still opens.
  if (hasNoSite(user) && !NO_SITE_OPEN.includes(pattern)) return false;
  // C14: the red Report incident is the technician's menu item only.
  if (TECH_ONLY_NAV.includes(pattern) && !isTechExperience(user)) return false;
  const employee = isEmployeeExperience(user);
  if (employee && !EMPLOYEE_NAV.includes(pattern)) return false;
  if (!employee && EMPLOYEE_ONLY_NAV.includes(pattern)) return false;
  const offer = NAV_OFFER[pattern];
  return offer === undefined || canOpen(user, offer);
}

/** The declared route a real pathname belongs to, or null when none matches. */
export function routeFor(pathname: string): { pattern: string; access: RouteAccess } | null {
  for (const [pattern, access] of Object.entries(ROUTE_ACCESS)) {
    if (matchPath({ path: pattern, end: true }, pathname)) return { pattern, access };
  }
  return null;
}
